// ReassignTable.jsx
import React from "react";
import {
    useTable,
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
} from "react-table";
import {
    ChevronDoubleLeftIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDoubleRightIcon,
} from "@heroicons/react/20/solid";
import GlobalFilter from "../../components/shared/table/GlobalFilter";

// If you already have Button/PageButton components, import those instead.
// Here are minimal fallbacks to keep this file drop-in.
function Button({ className = "", ...props }) {
    return (
        <button
            type="button"
            className={`px-3 py-2 text-sm border rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 ${className}`}
            {...props}
        />
    );
}
function PageButton(props) {
    return <Button {...props} />;
}

// Sort icons (simple inlined to avoid external deps)
function SortIcon({ className = "" }) {
    return (
        <svg className={className} viewBox="0 0 20 20" fill="currentColor">
            <path d="M3 7h14M3 13h9" />
        </svg>
    );
}
function SortUpIcon({ className = "" }) {
    return (
        <svg className={className} viewBox="0 0 20 20" fill="currentColor">
            <path d="M7 14l5-5 5 5H7z" />
        </svg>
    );
}
function SortDownIcon({ className = "" }) {
    return (
        <svg className={className} viewBox="0 0 20 20" fill="currentColor">
            <path d="M7 6l5 5 5-5H7z" />
        </svg>
    );
}

/**
 * ReassignTable
 * - Focused, minimal table for TeamsReassignOnDeletePage
 * - Supports global search, sorting, pagination
 * - Stable row ids and no auto-resets
 * - Emits onVisibleRowsChange (optional) when the visible page changes
 */
export default function ReassignTeamTable({ columns, data, onVisibleRowsChange }) {
    const visibleColumnCount = columns.length || 1;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page, // visible (paginated) rows
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state,
        preGlobalFilteredRows,
        setGlobalFilter,
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0, pageSize: 10 },
            getRowId: (row) => row.id, // stable id
            autoResetPage: false,
            autoResetSortBy: false,
            autoResetFilters: false,
            autoResetGlobalFilter: false,
            autoResetSelectedRows: false,
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    // Notify parent only when visible row IDs actually change
    const prevIdsRef = React.useRef([]);
    React.useEffect(() => {
        if (typeof onVisibleRowsChange !== "function") return;
        const ids = page.map((r) => r.original.id);
        const prev = prevIdsRef.current;
        const changed =
            ids.length !== prev.length || ids.some((id, i) => id !== prev[i]);
        if (changed) {
            prevIdsRef.current = ids;
            onVisibleRowsChange(page.map((r) => r.original));
        }
    }, [page, onVisibleRowsChange]);

    return (
        <>
            {/* Filters */}
            <div className="sm:flex sm:gap-x-2">
                <GlobalFilter
                    preGlobalFilteredRows={preGlobalFilteredRows}
                    globalFilter={state.globalFilter}
                    setGlobalFilter={setGlobalFilter}
                    gotoPage={gotoPage}
                    inputClassName="border-gray-300 rounded-md shadow-sm focus:border-teal-400 focus:ring focus:ring-teal-200 focus:ring-opacity-50"
                />
            </div>

            {/* Table */}
            <div className="flex flex-col mt-4">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <div className="overflow-hidden border border-gray-200 rounded-md shadow-sm">
                            <table
                                {...getTableProps()}
                                className="min-w-full divide-y divide-gray-200"
                            >
                                <thead className="bg-gray-50">
                                    {headerGroups.map((headerGroup) => (
                                        <tr {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map((column) => {
                                                const isSelectColumn =
                                                    column.id === "select";
                                                // No toggles de sort en columnas no ordenables (ni en 'select')
                                                const headerProps =
                                                    isSelectColumn ||
                                                    column.disableSortBy
                                                        ? column.getHeaderProps()
                                                        : column.getHeaderProps(
                                                              column.getSortByToggleProps()
                                                          );

                                                return (
                                                    <th
                                                        {...headerProps}
                                                        // mantén ancho fijo para que no colapse
                                                        style={{
                                                            width: column.width,
                                                            minWidth:
                                                                column.minWidth,
                                                            maxWidth:
                                                                column.maxWidth,
                                                        }}
                                                        className="px-6 py-3 text-xs font-medium tracking-normal text-left text-gray-600 uppercase group"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            {column.render("Header")}
                                                            {/* Oculta icono de sort en la columna select y en no-ordenables */}
                                                            {!(
                                                                isSelectColumn ||
                                                                column.disableSortBy
                                                            ) && (
                                                                <span>
                                                                    {column.isSorted ? (
                                                                        column.isSortedDesc ? (
                                                                            <SortDownIcon className="w-4 h-4 text-gray-400" />
                                                                        ) : (
                                                                            <SortUpIcon className="w-4 h-4 text-gray-400" />
                                                                        )
                                                                    ) : (
                                                                        <SortIcon className="w-4 h-4 text-gray-300 group-hover:text-gray-400" />
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </thead>

                                <tbody
                                    {...getTableBodyProps()}
                                    className="bg-white divide-y divide-gray-200"
                                >
                                    {page.length > 0 ? (
                                        page.map((row) => {
                                            prepareRow(row);
                                            return (
                                                <tr {...row.getRowProps()}>
                                                    {row.cells.map((cell) => (
                                                        <td
                                                            {...cell.getCellProps()}
                                                            className="relative z-0 px-6 py-4 overflow-hidden text-ellipsis"
                                                            role="cell"
                                                        >
                                                            {cell.column.Cell.name ===
                                                            "defaultRenderer" ? (
                                                                <div className="text-sm text-gray-600">
                                                                    {cell.render(
                                                                        "Cell"
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                cell.render("Cell")
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={visibleColumnCount}
                                                className="px-6 py-10 text-sm text-center text-gray-500"
                                            >
                                                {data.length === 0
                                                    ? "No data available"
                                                    : "No data found for the current search"}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between py-3">
                <div className="flex justify-between flex-1 sm:hidden">
                    <Button
                        onClick={() => previousPage()}
                        disabled={!canPreviousPage}
                    >
                        Previous
                    </Button>
                    <Button onClick={() => nextPage()} disabled={!canNextPage}>
                        Next
                    </Button>
                </div>

                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div className="flex items-baseline gap-x-2">
                        <span className="text-sm text-gray-700">
                            Page{" "}
                            <span className="font-medium">
                                {state.pageIndex + 1}
                            </span>{" "}
                            of{" "}
                            <span className="font-medium">{pageOptions.length}</span>
                        </span>
                        <label>
                            <span className="sr-only">Items per page</span>
                            <select
                                className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-teal-400 focus:ring focus:ring-teal-200 focus:ring-opacity-50"
                                value={state.pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                            >
                                {[10, 20, 50, 100].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        Show {pageSize}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div>
                        <nav
                            className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm"
                            aria-label="Pagination"
                        >
                            <PageButton
                                className="rounded-l-md"
                                onClick={() => gotoPage(0)}
                                disabled={!canPreviousPage}
                            >
                                <span className="sr-only">First</span>
                                <ChevronDoubleLeftIcon
                                    className="w-5 h-5 text-gray-400"
                                    aria-hidden="true"
                                />
                            </PageButton>
                            <PageButton
                                onClick={() => previousPage()}
                                disabled={!canPreviousPage}
                            >
                                <span className="sr-only">Previous</span>
                                <ChevronLeftIcon
                                    className="w-5 h-5 text-gray-400"
                                    aria-hidden="true"
                                />
                            </PageButton>
                            <PageButton
                                onClick={() => nextPage()}
                                disabled={!canNextPage}
                            >
                                <span className="sr-only">Next</span>
                                <ChevronRightIcon
                                    className="w-5 h-5 text-gray-400"
                                    aria-hidden="true"
                                />
                            </PageButton>
                            <PageButton
                                className="rounded-r-md"
                                onClick={() => gotoPage(pageCount - 1)}
                                disabled={!canNextPage}
                            >
                                <span className="sr-only">Last</span>
                                <ChevronDoubleRightIcon
                                    className="w-5 h-5 text-gray-400"
                                    aria-hidden="true"
                                />
                            </PageButton>
                        </nav>
                    </div>
                </div>
            </div>
        </>
    );
}
